import React from "react";
import { getFunctions, httpsCallable } from "firebase/functions";
import { Tooltip } from "react-tooltip";
import { getChampionById } from "constants/gameDefaults";
// Import images
import conflictIcon from "styles/images/feedback/conflict.png";
import handshakeIcon from "styles/images/feedback/handshake.png";
import swordIcon from "styles/images/feedback/sword.png";
import thumbsDownIcon from "styles/images/feedback/thumbs-down.png";
import thumbsUpIcon from "styles/images/feedback/thumbs-up.png";
import { capitalizeFirstLetter } from "./textUtils";
import app from "../firebaseConfig";

const interactionIconPositive = {
  Matchups: swordIcon,
  Champion: thumbsUpIcon,
  Synergies: handshakeIcon,
};

const interactionIconNegative = {
  Matchups: swordIcon,
  Champion: thumbsDownIcon,
  Synergies: conflictIcon,
};

const functions = getFunctions(app);
const fetchDraftereyeDataFunction = httpsCallable(
  functions,
  "fetchDraftereyeData",
);

export async function fetchDraftereyeData(allCharacters) {
  // Dev mode: return mock data to test UI without auth/API
  if (
    process.env.NODE_ENV === "development" &&
    process.env.REACT_APP_MOCK_API !== "false"
  ) {
    console.log("[DEV] Using mock DrafterEye data for:", allCharacters);
    const blueWinProb = 0.4 + Math.random() * 0.2; // 40-60%
    return {
      p_team1_wins: blueWinProb,
      p_team2_wins: 1 - blueWinProb,
      predicted_winner: blueWinProb > 0.5 ? 1 : 2,
      significant_matchups: [],
    };
  }

  try {
    const result = await fetchDraftereyeDataFunction(allCharacters);
    return result.data;
  } catch (error) {
    console.error("Error getting DrafterEye data:", error);
    throw error;
  }
}

function Matchup({
  champion1,
  champion2,
  typeOfInteraction,
  secondCharacter,
  direction,
  blueSideChampions,
}) {
  const isChampionInBlueSide = Object.values(blueSideChampions.team).includes(
    champion1,
  );
  const iconClass =
    secondCharacter || isChampionInBlueSide ? "champ1Icon" : "champ2Icon";

  const champion1Icon = getChampionById(champion1)?.fallBackIcon;
  const champion2Icon = getChampionById(champion2)?.fallBackIcon;

  const titleDirection = (() => {
    if (typeOfInteraction === "Champion") {
      return direction === "positive" ? "Strong" : "Weak";
    }
    return direction;
  })();

  return (
    <div className="matchupFeedback">
      <img
        key={champion1}
        src={champion1Icon}
        alt={champion1}
        className={iconClass}
      />

      <img
        key={direction}
        src={
          direction === "positive"
            ? interactionIconPositive[typeOfInteraction]
            : interactionIconNegative[typeOfInteraction]
        }
        data-tooltip-id={`${champion1}${typeOfInteraction}`}
        data-tooltip-content={`${capitalizeFirstLetter(
          titleDirection,
        )} ${typeOfInteraction}`}
        alt={direction}
        className={`interactionIcon ${typeOfInteraction} ${direction}`}
      />
      <Tooltip id={`${champion1}${typeOfInteraction}`} />

      {secondCharacter && (
        <img
          key={champion2}
          src={champion2Icon}
          alt={champion2}
          className="champ2Icon"
        />
      )}
    </div>
  );
}

export function FeedbackDisplay({ resultData, champions, blueSideChampions }) {
  const laneToIndex = {
    TOP_LANE_BLUE: 0,
    JUNGLE_BLUE: 1,
    MID_LANE_BLUE: 2,
    BOT_LANE_BLUE: 3,
    UTILITY_BLUE: 4,
    TOP_LANE_RED: 5,
    JUNGLE_RED: 6,
    MID_LANE_RED: 7,
    BOT_LANE_RED: 8,
    UTILITY_RED: 9,
  };

  if (!resultData) {
    return null;
  }

  const { significant_matchups: mostSignificantMatchups } = resultData;

  // safety check on roleWeights
  if (!mostSignificantMatchups) {
    return null;
  }

  return (
    <div className="Feedback feedbackContainer">
      {mostSignificantMatchups.map((feedback) => (
        <Matchup
          key={`{${feedback.initial_role}${feedback.interaction}}`}
          champion1={champions[laneToIndex[feedback.initial_role]]}
          champion2={champions[laneToIndex[feedback.interaction]]}
          typeOfInteraction={feedback.type}
          direction={feedback.direction}
          secondCharacter={feedback.type !== "Champion"}
          blueSideChampions={blueSideChampions}
        />
      ))}
    </div>
  );
}
